import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { apiCountries, apiRegions } from '../api/client.js';
import Modal from '../components/Modal.jsx';
import EntityActions from '../components/EntityActions.jsx';
import ArchivedFilter from '../components/ArchivedFilter.jsx';

/**
 * Sprint 5 — Vue CRUD admin des Régions.
 * PK = code ISO 3166-2 (ex: CH-VD). Code immuable à l'édition.
 */

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  background: 'var(--glass)',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 14,
};

const primaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 'var(--radius-pill)',
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const secondaryBtn = {
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '6px 14px',
  fontSize: 13,
  cursor: 'pointer',
};

const EMPTY = { code: '', name: '', countryCode: '' };

export default function RegionsView() {
  const [items, setItems] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [archivedFilter, setArchivedFilter] = useState('active');
  const [filterCountry, setFilterCountry] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items: data } = await apiRegions.list(filterCountry || undefined, archivedFilter);
      setItems(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [archivedFilter, filterCountry]);

  useEffect(() => {
    reload();
  }, [reload]);

  const loadCountries = async () => {
    const { items: data } = await apiCountries.list('active');
    setCountries(data);
  };

  useEffect(() => {
    loadCountries().catch(() => {});
  }, []);

  const openCreate = async () => {
    setEditing(null);
    setForm({ ...EMPTY, countryCode: filterCountry || countries[0]?.code || '' });
    setFormError(null);
    await loadCountries();
    setModalOpen(true);
  };

  const openEdit = async (r) => {
    setEditing(r);
    setForm({ code: r.code, name: r.name, countryCode: r.countryCode });
    setFormError(null);
    await loadCountries();
    setModalOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);
    try {
      if (editing) {
        await apiRegions.update(editing.code, {
          name: form.name.trim(),
          countryCode: form.countryCode,
        });
      } else {
        await apiRegions.create({
          code: form.code.trim().toUpperCase(),
          name: form.name.trim(),
          countryCode: form.countryCode,
        });
      }
      setModalOpen(false);
      await reload();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const onArchive = async (r) => {
    if (!window.confirm(`Archiver la région "${r.name}" (${r.code}) ?`)) return;
    await apiRegions.archive(r.code);
    await reload();
  };

  const onRestore = async (r) => {
    await apiRegions.restore(r.code);
    await reload();
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      <div
        style={{
          position: 'sticky',
          top: 0,
          background: 'var(--bg)',
          paddingBottom: 12,
          zIndex: 5,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          marginBottom: 16,
          flexWrap: 'wrap',
        }}
      >
        <h1 style={{ margin: 0, fontSize: 22, color: 'var(--text)' }}>Régions</h1>
        <ArchivedFilter value={archivedFilter} onChange={setArchivedFilter} />
        <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Pays :</span>
          <button
            type="button"
            onClick={() => setFilterCountry('')}
            style={{
              padding: '4px 12px',
              borderRadius: 999,
              border: '1px solid var(--glass-border)',
              background: filterCountry === '' ? 'var(--primary)' : 'var(--glass)',
              color: filterCountry === '' ? '#fff' : 'var(--text)',
              fontSize: 12,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Tous
          </button>
          {countries.map((c) => (
            <button
              key={c.code}
              type="button"
              onClick={() => setFilterCountry(c.code)}
              style={{
                padding: '4px 12px',
                borderRadius: 999,
                border: '1px solid var(--glass-border)',
                background: filterCountry === c.code ? 'var(--primary)' : 'var(--glass)',
                color: filterCountry === c.code ? '#fff' : 'var(--text)',
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {c.code}
            </button>
          ))}
        </div>
        <button type="button" onClick={openCreate} style={{ ...primaryBtn, marginLeft: 'auto' }}>
          <Plus size={14} /> Nouvelle région
        </button>
      </div>

      {error ? <div style={{ color: 'var(--danger)', marginBottom: 12 }}>{error}</div> : null}

      <div
        style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: 'var(--glass)', textAlign: 'left' }}>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500, width: 100 }}>Code</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Nom</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Pays</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Personnes</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Fériés</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500, width: 100 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding: 20, color: 'var(--text-muted)' }}>Chargement…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: 20, color: 'var(--text-muted)' }}>Aucune région.</td></tr>
            ) : (
              items.map((r) => (
                <tr key={r.code} style={{ borderTop: '1px solid var(--glass-border)' }}>
                  <td style={{ padding: '10px 12px', color: 'var(--text)', fontWeight: 600 }}>
                    {r.code}
                    {r.archivedAt ? (
                      <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted)' }}>(archivée)</span>
                    ) : null}
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--text)' }}>{r.name}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>
                    {r.country?.name ?? r.countryCode}
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{r._count?.persons ?? 0}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{r._count?.publicHolidays ?? 0}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <EntityActions
                      archived={Boolean(r.archivedAt)}
                      onEdit={() => openEdit(r)}
                      onArchive={() => onArchive(r)}
                      onRestore={() => onRestore(r)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? `Modifier ${editing.code}` : 'Nouvelle région'}
        footer={
          <>
            <button type="button" onClick={() => setModalOpen(false)} style={secondaryBtn}>
              Annuler
            </button>
            <button
              type="submit"
              form="region-form"
              disabled={
                submitting ||
                !form.name.trim() ||
                !form.countryCode ||
                (!editing && !/^[A-Za-z]{2}-[A-Za-z0-9]{1,3}$/.test(form.code.trim()))
              }
              style={{ ...primaryBtn, opacity: submitting ? 0.6 : 1 }}
            >
              Enregistrer
            </button>
          </>
        }
      >
        <form id="region-form" onSubmit={onSubmit} style={{ display: 'grid', gap: 12 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
              Code ISO 3166-2
            </label>
            <input
              type="text"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().slice(0, 8) })}
              required
              maxLength={8}
              disabled={Boolean(editing)}
              placeholder="CH-VD"
              autoFocus
              style={{ ...inputStyle, textTransform: 'uppercase', letterSpacing: '0.05em' }}
            />
            {editing ? (
              <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-muted)' }}>
                Le code n'est pas modifiable.
              </div>
            ) : null}
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
              Pays
            </label>
            <select
              value={form.countryCode}
              onChange={(e) => setForm({ ...form, countryCode: e.target.value })}
              required
              style={inputStyle}
            >
              <option value="">— Sélectionner un pays —</option>
              {countries.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code} — {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
              Nom
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              maxLength={64}
              placeholder="Vaud"
              style={inputStyle}
            />
          </div>
          {formError ? <div style={{ color: 'var(--danger)', fontSize: 13 }}>{formError}</div> : null}
        </form>
      </Modal>
    </div>
  );
}
